import { ISession } from "../../../../models/session";
import DateCol from "./DateCol";
import AttendanceCol from "./AttendanceCol";
import InfoCol from "./InfoCol";
import SessionContent from "./SessionContent";
import { useDispatch } from "react-redux";
import { addClassToCurrentUserClassList, removeClassToCurrentUserClassList } from "../../../../store/actions/current_userActions";
import { setMe } from "../../../../store/actions/authActions";
import sessionsService from "../../../../Axios/services/sessions.service";
import usersService from "../../../../Axios/services/users.service";
import styles from "./index.module.scss";
import { useSessionState } from "./hooks/useSessionState";
import { Fragment } from "react/jsx-runtime";

interface RegularSessionProps {
    session: ISession | ISession[];
    language: string;
    isFeed?: boolean,
    communities?: Array<any>
    loggedUser?: any,
    folderList?: Array<any>
}

const RegularSession = ({
    session,
    isFeed,
    communities,
    loggedUser,
    language,
    folderList
}: RegularSessionProps) => {
    const dispatch = useDispatch();
    const isNotEnglish = language !== "English";

    const {
        sessions,
        isSessionOpen,
        setIsSessionOpen,
        isUserRegistered,
        setIsUserRegistered,
        sessionUpdate,
        setSessionUpdate,
        entityLanguage,
        folderListBySession,
        dateColWidth,
        dateColRef
    } = useSessionState(session, loggedUser, folderList, language);

    const refreshUserClasses = async () => {
        const refreshed = await usersService.me();
        const classIds = Array.isArray(refreshed?.classes) ? refreshed.classes : [];
        const populated = classIds.length ? await sessionsService.getSessions(classIds) : [];
        dispatch(setMe({ ...loggedUser, classes: populated }));
    };

    const handleClassRegistration = async (id: string, sessionToRegister: ISession) => {
        try {
            setSessionUpdate(prev => ({ ...prev, [id]: true }));
            dispatch(addClassToCurrentUserClassList(sessionToRegister));

            const res = await sessionsService.registerToClass(id, loggedUser._id);
            if (res) {
                setIsUserRegistered(prev => ({ ...prev, [id]: true }));
                try {
                    await refreshUserClasses();
                } catch (refreshError) {
                    console.error('Error refreshing classes after registration:', refreshError);
                }
            }
        } catch (error) {
            console.error('Error registering for class:', error);
        } finally {
            setSessionUpdate(prev => ({ ...prev, [id]: false }));
        }
    };

    const handleClassUnregistration = async (id: string, sessionId: string) => {
        try {
            setSessionUpdate(prev => ({ ...prev, [id]: true }));
            dispatch(removeClassToCurrentUserClassList(sessionId));

            const res = await sessionsService.unRegisterToClass(id, loggedUser._id);
            if (res) {
                setIsUserRegistered(prev => ({ ...prev, [id]: false }));
                try {
                    await refreshUserClasses();
                } catch (refreshError) {
                    console.error('Error refreshing classes after cancellation:', refreshError);
                }
            }
        } catch (error) {
            console.error('Error unregistering from class:', error);
        } finally {
            setSessionUpdate(prev => ({ ...prev, [id]: false }));
        }
    };

    const toggleSession = (id: string) => {
        setIsSessionOpen(prev =>
            ({ ...prev, [id]: !Boolean(prev?.[id]) }));
    };

    const hasOpenSession = () => Object.values(isSessionOpen)
        .some(sessionId => Boolean(sessionId));

    const getSessionStyle = (sessionIndex: number) => {
        const isNotFirstSession = sessionIndex !== 0;

        if (isNotEnglish) {
            return isNotFirstSession ? {
                marginRight: `${dateColWidth - 1}px`,
                borderRight: "1px solid #f2f2f2",
            } : {};
        } else {
            return isNotFirstSession ? {
                marginLeft: `${dateColWidth - 1}px`,
                borderLeft: "1px solid #f2f2f2",
            } : {};
        }
    };

    return (
        <div className={styles.container}>
            <div className={`${styles.session} ${hasOpenSession() ? styles.openSession : ""} ${sessions.length !== 1 ? styles.multiSession : ""}`}>
                <div className={styles.sessionItems}>
                    {sessions.map((sessionItem, index) => {
                        const { _id, sessionDescription, address, related, sessionTitle, room, sessionTeam, zoomUrl, startTime, endTime, startDate, isToggle } = sessionItem;
                        const currentSessionIsOpen = Boolean(isSessionOpen?.[_id]);
                        const isUserRegisteredToSession = Boolean(isUserRegistered?.[_id]);
                        const isSessionUpdate = Boolean(sessionUpdate?.[_id]);
                        const folders = folderListBySession?.[_id] || [];
                        return (
                            <Fragment key={_id}>
                                <div className={styles.sessionItem} key={_id}>
                                    {index === 0 && (
                                        <div ref={dateColRef}>
                                            <DateCol
                                                startDate={startDate}
                                                startTime={startTime}
                                                endTime={endTime}
                                                language={language}
                                            />
                                        </div>
                                    )}
                                    <div className={`${styles.sessionItem} ${sessions.length !== 1 ? styles.borderBottom : ""}`}
                                        style={getSessionStyle(index)}>
                                        <InfoCol
                                            communities={communities}
                                            sessionTitle={sessionTitle}
                                            room={room}
                                            sessionUpdate={isSessionUpdate}
                                            sessionTeam={sessionTeam}
                                            entityLanguage={entityLanguage}

                                        />
                                        <AttendanceCol
                                            sessionId={_id}
                                            isSessionOpen={currentSessionIsOpen}
                                            entityLanguage={entityLanguage}
                                            isUserRegistered={isUserRegisteredToSession}
                                            sessionUpdate={isSessionUpdate}
                                            isFeed={isFeed}
                                            sessionDescription={sessionDescription}
                                            related={related}
                                            folderBySessionLength={folders.length}
                                            isToggle={isToggle}
                                            zoomUrl={zoomUrl}
                                            registerToClass={(id: string) => handleClassRegistration(id, sessionItem)}
                                            unregisterToClass={(id: string) => handleClassUnregistration(id, _id)}
                                            toggleEvent={() => toggleSession(_id)}
                                        />
                                    </div>

                                </div>
                                {currentSessionIsOpen &&
                                    <SessionContent
                                        sessionId={_id}
                                        sessionDescription={sessionDescription}
                                        zoomUrl={zoomUrl}
                                        sessionTitle={sessionTitle}
                                        related={related}
                                        folders={folders}
                                        startTime={startTime}
                                        endTime={endTime}
                                        address={address}
                                        room={room}
                                        startDate={startDate}
                                        entityLanguage={entityLanguage}
                                    />
                                }
                            </Fragment>)
                    })}
                </div>

            </div>
        </div>
    );
};

export default RegularSession;